import type { CodeableConcept } from '@medplum/fhirtypes'
import type { ExtendedResourceConstructor } from '@seen/core/ExtendedResource'
import { invariant } from '@seen/core/invariant'
import type { ExtendedLibrary } from '@seen/core/models/library'
import { Button } from '@seen/ui'
import { type JSX, useMemo, useState } from 'react'
import { Combobox, type ComboboxProps } from '#src/components/core/index.ts'
import { IconOpenInWindow } from '#src/components/icons/index.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

// Define the interface for template classes that have the required static properties
interface TemplateClass<
  T extends ExtendedLibrary,
> extends ExtendedResourceConstructor<T> {
  libraryTopic: CodeableConcept
}

export function TemplateCombobox<T extends ExtendedLibrary>({
  templateClass,
  onSelect,
  onOpenInNewWindow,
  ...delegated
}: {
  templateClass: TemplateClass<T>
  onSelect: (template: T) => any
  /**
   * When set, each row gets an "open in new window" button that invokes this
   * with the resolved template instead of selecting the row.
   */
  onOpenInNewWindow?: (template: T) => void
} & Omit<
  ComboboxProps<string>,
  'options' | 'onSelect' | 'open' | 'onOpenChange'
>): JSX.Element {
  const { data: templates } = useSearch(templateClass, {
    topic: templateClass.libraryTopic,
    'status:not': 'retired',
  })

  // Own the open state so the per-row button can close the popover directly:
  // its click stops propagation (to skip the row's same-tab navigation), which
  // also skips the onSelect path that normally closes the menu.
  const [open, setOpen] = useState(false)

  const options = useMemo(
    () =>
      templates?.map((template) => ({
        value: template.reference.reference,
        label: onOpenInNewWindow ? (
          <div className="flex w-full items-center gap-2">
            <span className="flex-1">{template.title}</span>
            <OpenInNewWindowButton
              onClick={() => {
                onOpenInNewWindow(template)
                setOpen(false)
              }}
            />
          </div>
        ) : (
          template.title
        ),
        keywords: [template.title],
      })) ?? [],
    [templates, onOpenInNewWindow]
  )

  return (
    <Combobox
      {...delegated}
      open={open}
      onOpenChange={setOpen}
      options={options}
      onSelect={(value) => {
        const template = templates?.find((t) => t.reference.reference === value)
        invariant(template, 'Template is required')
        onSelect(template)
      }}
    />
  )
}

function OpenInNewWindowButton({
  onClick,
}: {
  onClick: () => void
}): JSX.Element {
  // No tooltip: a per-row tooltip overlays adjacent rows and gets in the way of
  // hovering to other options. The glyph reads as "open in new window"; the
  // aria-label keeps it accessible.
  return (
    <Button
      variant="ghost"
      size="icon-sm"
      aria-label="Open in new window"
      className="-my-1 shrink-0 text-foreground-secondary"
      // Stop the click from selecting the row, which would navigate the current
      // tab instead of opening a new window.
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <IconOpenInWindow />
    </Button>
  )
}
